/* GGD-Inspectiepakket — Upload + Download UI
 *
 * KDV-houders (directeur/locatiemanager) beheren hier hun eigen documenten
 * (pedagogisch beleid, oudercommissie-notulen, klachtenregeling, VOG-scans, etc.)
 * die daarna automatisch worden meegenomen in de ZIP-download voor de GGD-inspecteur.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, Download, FileText, Trash2, Loader2, ShieldCheck, Package, Info, CheckCircle2,
  History, Clock,
} from "lucide-react";

const CATEGORIES = [
  { value: "pedagogisch_beleid", label: "Pedagogisch beleid" },
  { value: "veiligheidsbeleid", label: "Veiligheid- en gezondheidsbeleid" },
  { value: "klachtenregeling", label: "Klachtenregeling" },
  { value: "oudercommissie", label: "Oudercommissie (reglement / notulen)" },
  { value: "certificaten", label: "Certificaten / diploma-scans" },
  { value: "vog", label: "VOG-verklaringen" },
  { value: "ehbo_bhv", label: "EHBO / BHV-certificaten" },
  { value: "risicoinventarisatie", label: "Risico-inventarisatie" },
  { value: "meldcode", label: "Meldcode kindermishandeling" },
  { value: "overig", label: "Overig" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

function humanBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function InspectiepakketPage() {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [maxBytes, setMaxBytes] = useState(25 * 1024 * 1024);
  const [allowedExts, setAllowedExts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(null);

  const [categorie, setCategorie] = useState("pedagogisch_beleid");
  const [beschrijving, setBeschrijving] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, p, h] = await Promise.all([
        api.get("/directie/ggd/documenten"),
        api.get("/directie/ggd/inspectie/preview").catch(() => ({ data: null })),
        api.get("/directie/ggd/inspectie/historie?limit=50").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(r.data.items || []);
      setMaxBytes(r.data.max_bytes || maxBytes);
      setAllowedExts(r.data.allowed_extensions || []);
      setPreview(p.data);
      setHistory(h.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon inspectiedocumenten niet laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > maxBytes) {
      toast.error(`Bestand te groot: ${humanBytes(file.size)} > ${humanBytes(maxBytes)}`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("categorie", categorie);
      if (beschrijving.trim()) fd.append("beschrijving", beschrijving.trim());
      await api.post("/directie/ggd/documenten", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${file.name}" toegevoegd aan het inspectiepakket`);
      setBeschrijving("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload mislukt");
    } finally { setUploading(false); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Verwijder "${doc.filename}" uit het inspectiepakket?`)) return;
    try {
      await api.delete(`/directie/ggd/documenten/${doc.id}`);
      toast.success("Document verwijderd");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verwijderen mislukt");
    }
  };

  const handleIndividualDownload = async (doc) => {
    try {
      const r = await api.get(`/directie/ggd/documenten/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download mislukt");
    }
  };

  const handleZipDownload = async () => {
    setDownloading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const from = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      const r = await api.get(`/directie/ggd/inspectie.zip?date_from=${from}&date_to=${today}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ggd-inspectiepakket_${from}_${today}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Inspectiepakket gedownload — klaar voor de GGD-inspecteur");
      // Refresh historie zodat de nieuwe download meteen zichtbaar is
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download mislukt");
    } finally { setDownloading(false); }
  };

  const bytesSum = items.reduce((s, i) => s + (i.size_bytes || 0), 0);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <PageHeader
        title="GGD-Inspectiepakket"
        subtitle="Voeg eigen documenten toe (pedagogisch beleid, oudercommissie-notulen, VOG-scans, etc.) — deze komen automatisch mee in de download voor de GGD-inspecteur."
      />

      {/* Samenvatting-kaart */}
      <Card className="p-5" data-testid="inspectie-samenvatting">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)]">
              <Package className="h-6 w-6" />
            </div>
            <div>
              <div className="font-medium text-lg">Klaar voor de GGD-inspecteur</div>
              <div className="text-xs text-neutral-500">
                {preview?.inhoud?.length || 12} automatische bestanden
                {items.length > 0 && ` + ${items.length} eigen document(en) — totaal ${humanBytes(bytesSum)}`}
              </div>
            </div>
          </div>
          <Button onClick={handleZipDownload} disabled={downloading}
                  className="text-white" style={{ background: "var(--sage)" }}
                  data-testid="download-zip-btn">
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download compleet inspectiepakket (.zip)
          </Button>
        </div>
      </Card>

      {/* Upload-kaart */}
      <Card className="p-5 space-y-4" data-testid="inspectie-upload-card">
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          <h3 className="font-medium">Eigen document toevoegen</h3>
        </div>
        <p className="text-xs text-neutral-500">
          Max {humanBytes(maxBytes)} per bestand. Toegestaan: {allowedExts.join(", ")}.
          Bestanden worden veilig opgeslagen in Emergent Object Storage en zijn alleen zichtbaar
          voor jouw KDV.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Categorie</Label>
            <Select value={categorie} onValueChange={setCategorie}>
              <SelectTrigger data-testid="inspectie-categorie-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Beschrijving (optioneel)</Label>
            <Input value={beschrijving} onChange={e => setBeschrijving(e.target.value)}
                   placeholder="Bijv. Versie 2026-Q1"
                   maxLength={500}
                   data-testid="inspectie-beschrijving-input" />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="file"
                 className="hidden"
                 accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,image/webp,text/plain,text/csv"
                 onChange={e => handleUpload(e.target.files?.[0])}
                 disabled={uploading}
                 data-testid="inspectie-file-input" />
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border bg-white hover:bg-neutral-50 text-sm">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploaden…" : "Kies bestand"}
          </span>
        </label>
      </Card>

      {/* Documentenlijst */}
      <Card className="p-5" data-testid="inspectie-list-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" /> Eigen documenten
          </h3>
          <Badge variant="outline" data-testid="inspectie-doc-count">
            {items.length} document(en)
          </Badge>
        </div>
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map(i => <div key={i} className="h-14 bg-neutral-100 animate-pulse rounded" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-10 w-10 mx-auto text-neutral-300 mb-2" />
            <div className="text-neutral-500 mb-1">Nog geen eigen documenten geüpload</div>
            <div className="text-xs text-neutral-400">
              Voeg bijvoorbeeld het pedagogisch beleidsplan of oudercommissie-notulen toe.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {items.map(d => (
              <div key={d.id} className="py-3 flex items-center gap-3"
                   data-testid={`inspectie-doc-row-${d.id}`}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-neutral-100 text-neutral-500">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.filename}</div>
                  <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">
                      {CATEGORY_LABEL[d.categorie] || d.categorie}
                    </Badge>
                    <span>{humanBytes(d.size_bytes)}</span>
                    <span>·</span>
                    <span>door {d.uploaded_by_name}</span>
                    <span>·</span>
                    <span>{new Date(d.uploaded_at).toLocaleDateString("nl-NL")}</span>
                  </div>
                  {d.beschrijving && (
                    <div className="text-xs text-neutral-600 mt-0.5 italic">{d.beschrijving}</div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="sm"
                          onClick={() => handleIndividualDownload(d)}
                          title="Download dit bestand"
                          data-testid={`inspectie-doc-download-${d.id}`}>
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm"
                          onClick={() => handleDelete(d)}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          title="Verwijder uit inspectiepakket"
                          data-testid={`inspectie-doc-delete-${d.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Automatisch-mee-in-pakket kaart */}
      <Card className="p-5" data-testid="inspectie-preview-card">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          <h3 className="font-medium">Wat zit er ook automatisch in?</h3>
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          Deze rapporten worden bij elke download opnieuw gegenereerd uit de live data — je hoeft ze niet handmatig toe te voegen:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
          {(preview?.inhoud || []).map((line, i) => (
            <div key={i} className="flex items-center gap-1.5 text-neutral-700">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              <span className="truncate">{line}</span>
            </div>
          ))}
        </div>
        {preview?.counts && (
          <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-500 flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              Snapshot: {preview.counts.kinderen_actief} kinderen · {preview.counts.medewerkers_actief} medewerkers ·{" "}
              {preview.counts.groepen} groepen · {preview.counts.contracten_actief} actieve contracten ·{" "}
              {preview.counts.rooster_shifts} rooster-shifts (30d) ·{" "}
              {preview.counts.incidenten} incidenten (30d).
            </span>
          </div>
        )}
      </Card>

      {/* Bibliotheek — geschiedenis van gedownloade inspectiepakketten */}
      <Card className="p-5" data-testid="inspectie-historie-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium flex items-center gap-2">
            <History className="h-4 w-4" /> Downloadgeschiedenis
          </h3>
          <Badge variant="outline" data-testid="inspectie-historie-count">
            {history.length} download(s)
          </Badge>
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          Volledig audit-trail van elk gegenereerd inspectiepakket — wie, wanneer, welke periode.
          Deze lijst wordt automatisch bijgewerkt bij elke download.
        </p>
        {history.length === 0 ? (
          <div className="text-center py-8">
            <History className="h-10 w-10 mx-auto text-neutral-300 mb-2" />
            <div className="text-neutral-500">Nog geen inspectiepakketten gedownload</div>
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {history.map(h => {
              const dt = new Date(h.downloaded_at);
              const mb = ((h.bytes || 0) / (1024 * 1024)).toFixed(2);
              const totalFiles = (h.n_auto_files || 12) + (h.n_own_docs || 0);
              return (
                <div key={h.id} className="py-3 flex items-center gap-3"
                     data-testid={`inspectie-historie-row-${h.id}`}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)] shrink-0">
                    <Package className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">
                      Periode {h.date_from} → {h.date_to}
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        {totalFiles} bestand(en)
                      </Badge>
                      {h.n_own_docs > 0 && (
                        <Badge variant="outline" className="ml-1 text-[10px] border-emerald-200 text-emerald-700">
                          +{h.n_own_docs} eigen
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-neutral-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                      <Clock className="h-3 w-3" />
                      <span>{dt.toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })}</span>
                      <span>·</span>
                      <span>door <strong>{h.downloaded_by_name || h.downloaded_by_email || "onbekend"}</strong></span>
                      {h.downloaded_by_role && (
                        <>
                          <span>·</span>
                          <span className="capitalize">{h.downloaded_by_role}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>{mb} MB</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
